/**
 * Gateway `Notice` codes on the md socket (crates/gateway session.rs, ws.rs, history.rs) and the
 * one-line texts the web client shows for them (SPEC §6.12, §7.3). Pure: no protobuf, no DOM.
 */

/** Pushes were lost (ticker lag); `text` starts with `"<contract>: "`. Back-fill from history. */
export const NOTICE_MD_GAP = "MD_GAP";
/** A subscribe would exceed the gateway's per-connection symbol cap (64). */
export const NOTICE_MD_SYMBOL_LIMIT = "MD_SYMBOL_LIMIT";
/** More than 16 subscribe/history requests in flight on one socket. */
export const NOTICE_TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS";
/** A history request spans more than `GATEWAY_MAX_WINDOWS_PER_REQUEST` replay windows. */
export const NOTICE_HISTORY_RANGE_TOO_LARGE = "HISTORY_RANGE_TOO_LARGE";
/**
 * After a ticker-plant reconnect the gateway could not re-subscribe the contract and keeps
 * retrying (CB3); `text` starts with `"<contract>: "`. Live data for it is not flowing.
 */
export const NOTICE_MD_RESUBSCRIBE_FAILED = "MD_RESUBSCRIBE_FAILED";

/** Structural subset of `helm.v1.Notice`. */
export type NoticeLike = { code: string; text: string; level?: number };

/** The contract a notice is about, from the gateway's `"<symbol>: …"` text convention. */
export function noticeSymbol(text: string): string | null {
  const i = text.indexOf(": ");
  if (i <= 0) return null;
  const sym = text.slice(0, i);
  return /^[A-Za-z0-9._/-]+$/.test(sym) ? sym : null;
}

/**
 * Concise user text for a notice (toast / chart overlay). `symbol` is what the user picked
 * ("NQ"); falls back to the contract in the text, then to nothing.
 */
export function noticeMessage(n: NoticeLike, symbol: string | null = null): string {
  const sym = symbol ?? noticeSymbol(n.text);
  const on = sym ? ` (${sym})` : "";
  switch (n.code) {
    case NOTICE_MD_GAP:
      return `Market data gap${on}: updates were dropped — back-filling from history`;
    case NOTICE_MD_SYMBOL_LIMIT:
      return `Symbol limit reached${on}: this window streams at most 64 symbols — close some widgets`;
    case NOTICE_TOO_MANY_REQUESTS:
      return `Gateway busy${on}: too many requests in flight — retrying`;
    case NOTICE_HISTORY_RANGE_TOO_LARGE:
      return `History range too large${on} — showing what loaded`;
    case NOTICE_MD_RESUBSCRIBE_FAILED:
      return `Live data not restored${on}: the gateway could not re-subscribe after a reconnect — still retrying`;
    default: {
      const t = n.text.trim();
      return t.length > 0 ? t : n.code;
    }
  }
}

/** Structural subset of `helm.v1.MarketDataGap` (the gateway's reconnect gap, CB3). */
export type MdGapLike = { symbol: string; fromNs: bigint; toNs: bigint; reason?: string };

/** A chart as far as a gap is concerned: its id and the contract it currently shows. */
export type GapChart = { id: string; resolved: string | null };

export type GapBackfill = {
  /** The outage window, epoch ms (`from` <= `to`): bars overlapping it are not seam-checked. */
  outage: { from: number; to: number };
  /** Charts on the gap's contract: each back-fills from its covered end (`HistoryLoader.resume`). */
  chartIds: string[];
};

/**
 * What a `MarketDataGap` asks of the charts: the outage window (ms) and which charts back-fill it
 * through their existing history path. `null` for a gap that names no contract or no window.
 */
export function planGapBackfill(gap: MdGapLike, charts: Iterable<GapChart>): GapBackfill | null {
  if (gap.symbol.length === 0 || gap.toNs === 0n) return null;
  const to = Number(gap.toNs / 1_000_000n);
  const from = Math.min(Number(gap.fromNs / 1_000_000n), to);
  const chartIds: string[] = [];
  for (const c of charts) if (c.resolved === gap.symbol) chartIds.push(c.id);
  return { outage: { from, to }, chartIds };
}

/** User text for a reconnect gap ("Market data gap (NQ): ticker plant reconnected after 12 s — …"). */
export function gapMessage(gap: MdGapLike, symbol: string | null = null): string {
  const sym = symbol ?? gap.symbol;
  const secs = Number((gap.toNs > gap.fromNs ? gap.toNs - gap.fromNs : 0n) / 1_000_000n) / 1000;
  const took = secs >= 1 ? ` after ${secs < 10 ? secs.toFixed(1) : String(Math.round(secs))} s` : "";
  return `Market data gap${sym ? ` (${sym})` : ""}: ticker plant reconnected${took} — back-filling from history`;
}
